import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Popover, Spin, Tabs, Tree, message } from 'antd';
import { CaretDownOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import API, { commonApiInvoker } from '@api/api';
import { RESOURCE_PARAMS, RESOURCE_TYPE } from '@utils/CONST';
import { AppContext } from '@utils/AppContext';
import Utils from '@utils/utils';
import Iconfont from '@utils/Iconfont';
import SelectionItem from '@view/page_v3/LeftComp/components/SelectionItem';
import EnterpriseEmpty from '@view/page_v3/MaterialManage/components/EnterpriseEmpty';
import styles from './index.module.scss';

const BASE_URL = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/freeCanvas';
const APPLY_TO_CANVAS_ICON = `${BASE_URL}/apply.svg`;
const RESOURCE_TYPE_FOR_LIBRARY = RESOURCE_TYPE.SIM_TRYON;
const CARD_RESOURCE_TYPE = RESOURCE_TYPE.TRYON_MODEL;
const MATERIAL_PAGE_SIZE = 60;

const transformFolderTreeData = (list = []) => list.map((item) => {
    const node = {
        key: item?.id,
        title: item?.name || '',
        count: item?.imageCount ?? item?.resCount ?? item?.count ?? 0,
        isLeaf: !item?.children || item.children.length === 0,
    };

    if (Array.isArray(item?.children) && item.children.length > 0) {
        node.children = transformFolderTreeData(item.children);
    }

    return node;
});

const getAncestorKeysByFolderId = (list, targetId, ancestors = []) => {
    if (!Array.isArray(list) || !targetId) return null;

    for (const item of list) {
        const nextAncestors = [...ancestors, item.id];

        if (item.id === targetId) {
            return ancestors;
        }

        if (Array.isArray(item.children) && item.children.length > 0) {
            const found = getAncestorKeysByFolderId(item.children, targetId, nextAncestors);
            if (found) return found;
        }
    }

    return null;
};

const getSelectKey = (item) => item?.key || item?.name || item?.id;
const getMaterialImageUrl = (item) => item?.imageUrlWithVersion || item?.imageUrl || '';

const formatMaterialList = (items = []) => items
    .filter(Boolean)
    .map((item) => ({
        ...item,
        key: getSelectKey(item),
        canManage: false,
        attr: {
            ...(item?.attr || {}),
            title: item?.attr?.title || item?.name || '',
        },
        subList: Array.isArray(item?.subList) ? item.subList : [],
        isFavorite: !!item?.attr?.isFavorite,
        isCustom: true,
        imageUrlWithVersion: Utils.addParamsToUrl(item?.imageUrl, { version: item?.attr?.version }),
    }));

const MaterialLibraryModal = ({
    open = false,
    onClose,
    onSelect,
    showApplyAction = true,
    multiple = true,
    maxSelectCount = 1,
    initialSelectedItems,
}) => {
    const { globalOrgInfo } = useContext(AppContext);
    const [activeTab, setActiveTab] = useState('personal');
    const [folderList, setFolderList] = useState([]);
    const [folderTotal, setFolderTotal] = useState(0);
    const [folderOpen, setFolderOpen] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [activeFolder, setActiveFolder] = useState('refImage_all');
    const [activeFolderName, setActiveFolderName] = useState('全部参考图');
    const [keyword, setKeyword] = useState('');
    const [materials, setMaterials] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isEmpty, setIsEmpty] = useState(false);
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const requestIdRef = useRef(0);

    const isEnterpriseEmpty = activeTab === 'enterprise' && (globalOrgInfo?.isPersonal || !localStorage.getItem('token'));
    const treeData = useMemo(() => transformFolderTreeData(folderList), [folderList]);
    const normalizedMaxSelectCount = Math.max(1, Number(maxSelectCount) || 1);
    const selectedMaterialKeySet = useMemo(
        () => new Set(selectedMaterials.map((item) => String(getSelectKey(item)))),
        [selectedMaterials],
    );

    const expandFolderAncestors = useCallback((folderId) => {
        if (!folderId || folderId === 'refImage_all') return;
        const ancestors = getAncestorKeysByFolderId(folderList, folderId) || [];
        if (!ancestors.length) return;
        setExpandedKeys((prev) => Array.from(new Set([...(prev || []), ...ancestors])));
    }, [folderList]);

    const loadFolderTree = useCallback(() => {
        API.materialFolderTree({ scope: activeTab }).then((res) => {
            if (res?.code === 0) {
                const nextTree = Array.isArray(res?.data) ? res.data : [];
                setFolderList(nextTree);
                setFolderTotal(res?.attr?.total ?? 0);
                return;
            }

            setFolderList([]);
            setFolderTotal(0);
        }).catch(() => {
            setFolderList([]);
            setFolderTotal(0);
        });
    }, [activeTab]);

    const loadMaterialList = useCallback(async (nextPageIndex = 1, reset = false) => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        if (reset) {
            setInitialLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = {
                ...RESOURCE_PARAMS[RESOURCE_TYPE_FOR_LIBRARY],
                scope: activeTab,
                folderId: activeFolder === 'refImage_all' ? '' : activeFolder,
                keyword,
                pageIndex: nextPageIndex,
                pageSize: MATERIAL_PAGE_SIZE,
                withAttr: true,
            };
            const res = await commonApiInvoker(API.materialList(params));

            if (requestIdRef.current !== requestId) return;

            const images = res?.images || {};
            const total = images?.total || 0;
            const nextList = formatMaterialList(images?.items || []);

            if (reset) {
                setMaterials(nextList);
                setIsEmpty(total === 0);
            } else {
                setMaterials((prev) => prev.concat(nextList));
            }

            setPageIndex(nextPageIndex);
            setHasMore(nextPageIndex * MATERIAL_PAGE_SIZE < total);
        } finally {
            if (requestIdRef.current === requestId) {
                setInitialLoading(false);
                setLoadingMore(false);
            }
        }
    }, [activeFolder, activeTab, keyword]);

    useEffect(() => {
        if (!open) return;
        if (isEnterpriseEmpty) {
            setFolderList([]);
            setFolderTotal(0);
            return;
        }
        loadFolderTree();
    }, [isEnterpriseEmpty, open, loadFolderTree]);

    useEffect(() => {
        if (!open) return undefined;
        if (isEnterpriseEmpty) return undefined;

        const timer = window.setTimeout(() => {
            loadMaterialList(1, true);
        }, 250);

        return () => window.clearTimeout(timer);
    }, [open, activeFolder, keyword, loadMaterialList, isEnterpriseEmpty]);

    useEffect(() => {
        if (!open) return;
        expandFolderAncestors(activeFolder);
    }, [open, activeFolder, expandFolderAncestors]);

    useEffect(() => {
        if (!open) {
            setFolderOpen(false);
            setExpandedKeys([]);
            setActiveTab('personal');
            setActiveFolder('refImage_all');
            setActiveFolderName('全部参考图');
            setKeyword('');
            setMaterials([]);
            setPageIndex(1);
            setHasMore(false);
            setInitialLoading(false);
            setLoadingMore(false);
            setIsEmpty(false);
            setSelectedMaterials([]);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !multiple || !Array.isArray(initialSelectedItems)) return;
        setSelectedMaterials(initialSelectedItems.filter(Boolean));
    }, [initialSelectedItems, multiple, open]);

    const handleTabChange = useCallback((nextTab) => {
        setActiveTab(nextTab);
        setFolderOpen(false);
        setExpandedKeys([]);
        setActiveFolder('refImage_all');
        setActiveFolderName('全部参考图');
        setKeyword('');
        setMaterials([]);
        setPageIndex(1);
        setHasMore(false);
        setInitialLoading(false);
        setLoadingMore(false);
        setIsEmpty(false);
    }, []);

    const handleFolderSelect = useCallback((folderId, folderName) => {
        setActiveFolder(folderId);
        setActiveFolderName(folderName);
        setFolderOpen(false);
        expandFolderAncestors(folderId);
    }, [expandFolderAncestors]);

    const handleScroll = useCallback((event) => {
        const target = event.currentTarget;
        const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

        if (!reachedBottom || loadingMore || initialLoading || !hasMore) return;
        loadMaterialList(pageIndex + 1, false);
    }, [hasMore, initialLoading, loadMaterialList, loadingMore, pageIndex]);

    const handleWheelCapture = useCallback((event) => {
        event.stopPropagation();
    }, []);

    const handleSelectMaterial = useCallback((item) => {
        const imageUrl = item?.imageUrlWithVersion || item?.imageUrl || '';
        if (!imageUrl) return;
        if (multiple) {
            const itemKey = String(getSelectKey(item));
            setSelectedMaterials((prev) => {
                const exists = prev.some((selectedItem) => String(getSelectKey(selectedItem)) === itemKey);
                if (exists) {
                    return prev.filter((selectedItem) => String(getSelectKey(selectedItem)) !== itemKey);
                }
                if (prev.length >= normalizedMaxSelectCount) {
                    message.warning(`最多选择${normalizedMaxSelectCount}张图片`);
                    return prev;
                }
                return prev.concat(item);
            });
            return;
        }
        onSelect?.(imageUrl, item);
        onClose?.();
    }, [multiple, normalizedMaxSelectCount, onClose, onSelect]);

    const handleRemoveSelectedMaterial = useCallback((materialKey) => {
        setSelectedMaterials((prev) => prev.filter((item) => String(getSelectKey(item)) !== String(materialKey)));
    }, []);

    const handleConfirmSelection = useCallback(() => {
        if (!multiple) return;
        const selectedUrls = selectedMaterials
            .map((item) => getMaterialImageUrl(item))
            .filter(Boolean);
        if (selectedUrls.length === 0) {
            message.warning('请先选择图片');
            return;
        }
        onSelect?.(selectedUrls, selectedMaterials);
        onClose?.();
    }, [multiple, onClose, onSelect, selectedMaterials]);

    const folderTitleRender = useCallback((nodeData) => {
        const isActive = activeFolder === nodeData.key;
        const isExpanded = expandedKeys.includes(nodeData.key);
        const hasChildren = Array.isArray(nodeData.children) && nodeData.children.length > 0;
        const icon = isActive || (hasChildren && isExpanded)
            ? <Iconfont type="icon-Material-FolderOpenOutlined" />
            : <Iconfont type="icon-Material-FolderOutlined" />;

        return (
            <div className={styles.treeNodeRow}>
                <div className={`${styles.treeNodeContent} ${isActive ? styles.activeNode : ''}`}>
                    {hasChildren ? (
                        <span
                            className={`${styles.customSwitcher} ${isExpanded ? styles.expanded : ''}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setExpandedKeys((prev) => (
                                    prev.includes(nodeData.key)
                                        ? prev.filter((key) => key !== nodeData.key)
                                        : prev.concat(nodeData.key)
                                ));
                            }}
                        >
                            <CaretDownOutlined />
                        </span>
                    ) : (
                        <span className={styles.switcherPlaceholder} />
                    )}
                    <span className={styles.nodeIcon}>{icon}</span>
                    <span className={styles.nodeTitle}>{nodeData.title}</span>
                    <span className={styles.nodeCount}>{nodeData.count}</span>
                </div>
            </div>
        );
    }, [activeFolder, expandedKeys]);

    return (
        <Modal
            open={open}
            title="素材库"
            onCancel={onClose}
            footer={null}
            width={multiple ? 1224 : 1030}
            centered
            destroyOnClose={false}
            className={styles.modal}
        >
            <div className={styles.content} onWheel={handleWheelCapture}>
                <div className={styles.tabHeader}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        tabBarExtraContent={!isEnterpriseEmpty ? (
                            <div className={styles.header}>
                                <Popover
                                    trigger="click"
                                    placement="bottomLeft"
                                    open={folderOpen}
                                    onOpenChange={setFolderOpen}
                                    arrow={false}
                                    getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                                    classNames={{ root: styles.folderPopover }}
                                    content={(
                                        <div
                                            className={`${styles.folderDropdown} _beauty_scrollbar`}
                                            onMouseDown={(event) => event.stopPropagation()}
                                            onClick={(event) => event.stopPropagation()}
                                            onWheel={handleWheelCapture}
                                        >
                                            <div
                                                className={`${styles.treeNodeContent} ${activeFolder === 'refImage_all' ? styles.activeNode : ''}`}
                                                onClick={() => handleFolderSelect('refImage_all', '全部参考图')}
                                            >
                                                <span className={styles.switcherPlaceholder} />
                                                <span className={styles.nodeIcon}>
                                                    {activeFolder === 'refImage_all'
                                                        ? <Iconfont type="icon-Material-FolderOpenOutlined" />
                                                        : <Iconfont type="icon-Material-FolderOutlined" />}
                                                </span>
                                                <span className={styles.nodeTitle}>全部参考图</span>
                                                <span className={styles.nodeCount}>{folderTotal}</span>
                                            </div>
                                            <Tree
                                                treeData={treeData}
                                                expandedKeys={expandedKeys}
                                                onExpand={(keys) => setExpandedKeys(keys)}
                                                selectedKeys={activeFolder === 'refImage_all' ? [] : [activeFolder]}
                                                onSelect={(selectedKeys, info) => {
                                                    if (selectedKeys.length > 0) {
                                                        handleFolderSelect(selectedKeys[0], info?.node?.title || '');
                                                    }
                                                }}
                                                titleRender={folderTitleRender}
                                                blockNode
                                                showIcon={false}
                                                switcherIcon={null}
                                                className={styles.customTree}
                                            />
                                        </div>
                                    )}
                                >
                                    <div
                                        className={styles.folderSelect}
                                        title={activeFolderName}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <span className={styles.folderSelectName}>{activeFolderName}</span>
                                        <CaretDownOutlined className={`${styles.folderSelectArrow} ${folderOpen ? styles.open : ''}`} />
                                    </div>
                                </Popover>
                                <Input
                                    className={styles.searchInput}
                                    placeholder="搜索内容"
                                    prefix={<SearchOutlined style={{ color: '#BFBFBF' }} />}
                                    value={keyword}
                                    onChange={(event) => setKeyword(event.target.value)}
                                    allowClear
                                />
                            </div>
                        ) : null}
                        items={[
                            { key: 'personal', label: '个人素材' },
                            { key: 'enterprise', label: '企业素材' },
                        ]}
                    />
                </div>
                {isEnterpriseEmpty ? (
                    <div className={styles.enterpriseEmptyWrap}>
                        <EnterpriseEmpty />
                    </div>
                ) : null}
                {!isEnterpriseEmpty ? (
                <div className={styles.body}>
                    <div
                        className={`${styles.list} _beauty_scrollbar`}
                        onScroll={handleScroll}
                        onWheel={handleWheelCapture}
                    >
                        {initialLoading ? (
                            <div className={styles.loadingState}>
                                <Spin size="large" />
                            </div>
                        ) : null}
                        {!initialLoading && isEmpty ? (
                            <div className={styles.emptyState}>
                                <img src="https://photostudio-ai.oss-cn-hangzhou.aliyuncs.com/photostudio-web/admin/images_v3/icon_empty.svg" alt="" />
                                <span>这里什么也没有哦~</span>
                            </div>
                        ) : null}
                        <div className={styles.grid}>
                            {materials.map((item) => {
                                const itemKey = String(getSelectKey(item));
                                const isSelected = selectedMaterialKeySet.has(itemKey);
                                const isOverflow = multiple && !isSelected && selectedMaterials.length >= normalizedMaxSelectCount;

                                return (
                                    <SelectionItem
                                        key={item.id}
                                        className={styles.card}
                                        hoverActionButtonClassName={showApplyAction ? styles.cardApplyButton : ''}
                                        hoverActionContent={showApplyAction ? <img src={APPLY_TO_CANVAS_ICON} alt="应用到画布" className={styles.cardApplyIcon} draggable={false} /> : null}
                                        record={item}
                                        showName
                                        multiple={multiple}
                                        actived={isSelected}
                                        activeClassName={multiple ? styles.cardActiveThin : ''}
                                        hideCheckbox={multiple}
                                        overflow={isOverflow}
                                        overflowTips={`最多选择${normalizedMaxSelectCount}张图片`}
                                        favorDisabled
                                        onClick={handleSelectMaterial}
                                        onEditImageAction={showApplyAction ? handleSelectMaterial : null}
                                        onMoreClick={() => {}}
                                        resourceType={CARD_RESOURCE_TYPE}
                                        materialScope={activeTab}
                                    />
                                );
                            })}
                        </div>
                        {loadingMore ? (
                            <div className={styles.loadingMore}>加载中...</div>
                        ) : null}
                    </div>
                    {multiple ? (
                        <div className={`${styles.selectedPane} ${styles.selectedPaneScrollable} _beauty_scrollbar`}>
                            <p className={styles.selectedTitle}>已选图片 {selectedMaterials.length}/{normalizedMaxSelectCount}</p>
                            <div className={styles.selectedGrid}>
                                {selectedMaterials.map((item) => {
                                    const materialKey = getSelectKey(item);
                                    const imageUrl = getMaterialImageUrl(item);

                                    return (
                                        <div key={materialKey} className={styles.selectedItem}>
                                            <img src={Utils.getImgThumbnail(imageUrl)} alt="" className={styles.selectedImage} />
                                            <button
                                                type="button"
                                                className={styles.selectedRemove}
                                                onClick={() => handleRemoveSelectedMaterial(materialKey)}
                                                aria-label="移除已选图片"
                                            >
                                                <CloseOutlined />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
                ) : null}
                {multiple && !isEnterpriseEmpty ? (
                    <div className={styles.footer}>
                        <Button className={styles.footerButton} onClick={onClose}>取消</Button>
                        <Button type="primary" className={styles.footerButton} onClick={handleConfirmSelection}>确定</Button>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default MaterialLibraryModal;
